/**
 * Voucher detail page with copy-to-clipboard functionality
 */
import React, { useEffect } from 'react';
import { ArrowLeft, Trash2, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useVoucherStore } from '@/store/voucherStore';
import { Loading } from '@/components/Loading';
import { CopyButton } from '@/components/CopyButton';
import { formatCurrency, formatDate, formatDateTime, isExpired, isExpiringSoon } from '@/utils/dateUtils';
import { VoucherStatus } from '@/types';
import { toast } from 'react-toastify';

function DetailRow({
  label,
  value,
  copyable,
}: {
  label: string;
  value: string | number | null | undefined;
  copyable?: boolean;
}) {
  return (
    <div className="mb-4">
      <p className="mb-1 text-xs text-muted-foreground">{label}</p>
      <div className="flex items-center gap-2">
        <span>{value ?? 'N/A'}</span>
        {copyable && value != null && value !== '' && (
          <CopyButton text={String(value)} label={label} size="small" />
        )}
      </div>
    </div>
  );
}

export const VoucherDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    selectedVoucher,
    isLoading,
    fetchVoucher,
    deleteVoucher,
    markVoucherUsed,
    clearSelectedVoucher,
  } = useVoucherStore();

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [useDialogOpen, setUseDialogOpen] = React.useState(false);

  useEffect(() => {
    if (id) {
      fetchVoucher(id);
    }
    return () => {
      clearSelectedVoucher();
    };
  }, [id, fetchVoucher, clearSelectedVoucher]);

  const handleDelete = async () => {
    if (selectedVoucher) {
      try {
        await deleteVoucher(selectedVoucher.id);
        toast.success('Voucher deleted successfully');
        navigate('/vouchers');
      } catch {
        toast.error('Failed to delete voucher');
      }
    }
  };

  const handleMarkUsed = async () => {
    if (selectedVoucher) {
      try {
        await markVoucherUsed(selectedVoucher.id);
        toast.success('Voucher marked as used');
        setUseDialogOpen(false);
      } catch {
        toast.error('Failed to mark voucher as used');
      }
    }
  };

  if (isLoading) {
    return <Loading message="Loading voucher details..." />;
  }

  if (!selectedVoucher) {
    return (
      <div>
        <Button variant="ghost" className="mb-4 gap-2" onClick={() => navigate('/vouchers')}>
          <ArrowLeft className="size-4" />
          Back to Vouchers
        </Button>
        <p className="text-destructive font-medium">Voucher not found</p>
      </div>
    );
  }

  const expiryWarning = isExpired(selectedVoucher.expiry_date)
    ? { variant: 'destructive' as const, label: 'Expired', icon: <XCircle className="size-4" /> }
    : isExpiringSoon(selectedVoucher.expiry_date)
      ? { variant: 'outline' as const, label: 'Expiring Soon', icon: <AlertTriangle className="size-4" /> }
      : null;

  const getStatusVariant = (status: VoucherStatus): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case VoucherStatus.ACTIVE:
        return 'default';
      case VoucherStatus.USED:
        return 'secondary';
      case VoucherStatus.EXPIRED:
        return 'destructive';
      default:
        return 'outline';
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <Button variant="ghost" className="gap-2" onClick={() => navigate('/vouchers')}>
          <ArrowLeft className="size-4" />
          Back to Vouchers
        </Button>
        {selectedVoucher.status === VoucherStatus.ACTIVE && (
          <div className="flex gap-2">
            <Button className="gap-2" onClick={() => setUseDialogOpen(true)}>
              <CheckCircle className="size-4" />
              Mark as Used
            </Button>
            <Button variant="outline" className="gap-2 text-destructive" onClick={() => setDeleteDialogOpen(true)}>
              <Trash2 className="size-4" />
              Delete
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-6 md:flex-row">
        <div className="flex-1 md:flex-[2]">
          <Card>
            <CardContent className="pt-6">
              <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h1 className="mb-2 text-2xl font-semibold">{selectedVoucher.brand_name}</h1>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={getStatusVariant(selectedVoucher.status)}>
                      {selectedVoucher.status}
                    </Badge>
                    {expiryWarning && (
                      <Badge variant={expiryWarning.variant} className="gap-1">
                        {expiryWarning.icon}
                        {expiryWarning.label}
                      </Badge>
                    )}
                  </div>
                </div>
                <p className="text-xl font-semibold text-primary">
                  {formatCurrency(selectedVoucher.value)}
                </p>
              </div>

              <Separator className="my-6" />

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <DetailRow label="Coupon Code" value={selectedVoucher.coupon_code} copyable />
                  {selectedVoucher.pin && (
                    <DetailRow label="PIN" value={selectedVoucher.pin} copyable />
                  )}
                  <DetailRow label="GCID" value={selectedVoucher.gcid} copyable />
                  <DetailRow label="Source Platform" value={selectedVoucher.source_platform} />
                </div>
                <div>
                  <DetailRow
                    label="Expiry Date"
                    value={selectedVoucher.expiry_date ? formatDate(selectedVoucher.expiry_date) : undefined}
                  />
                  <DetailRow
                    label="Purchase Date"
                    value={selectedVoucher.purchase_date ? formatDate(selectedVoucher.purchase_date) : undefined}
                  />
                  <DetailRow
                    label="Issuance Date"
                    value={selectedVoucher.issuance_date ? formatDateTime(selectedVoucher.issuance_date) : undefined}
                  />
                  {selectedVoucher.used_date && (
                    <DetailRow label="Used Date" value={formatDateTime(selectedVoucher.used_date)} />
                  )}
                </div>
              </div>

              {selectedVoucher.redemption_link && (
                <div className="mt-6">
                  <p className="mb-1 text-xs text-muted-foreground">Redemption Link</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <a
                      href={selectedVoucher.redemption_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline-offset-4 hover:underline"
                    >
                      {selectedVoucher.redemption_link}
                    </a>
                    <CopyButton text={selectedVoucher.redemption_link} label="Link" size="small" />
                  </div>
                </div>
              )}

              {selectedVoucher.claim_link && (
                <div className="mt-6">
                  <p className="mb-1 text-xs text-muted-foreground">Claim Link</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <a
                      href={selectedVoucher.claim_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline-offset-4 hover:underline"
                    >
                      {selectedVoucher.claim_link}
                    </a>
                    <CopyButton text={selectedVoucher.claim_link} label="Link" size="small" />
                  </div>
                </div>
              )}

              {selectedVoucher.notes && (
                <div className="mt-6">
                  <p className="mb-2 text-xs text-muted-foreground">Notes</p>
                  <div className="rounded-lg border bg-muted/50 p-4">
                    <p className="text-sm">{selectedVoucher.notes}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="flex-1">
          <Card>
            <CardContent className="pt-6">
              <h2 className="mb-4 text-lg font-semibold">Voucher Information</h2>
              <Separator className="mb-4" />
              <DetailRow label="Voucher ID" value={selectedVoucher.id} copyable />
              <DetailRow label="Created At" value={formatDateTime(selectedVoucher.created_at)} />
              <DetailRow label="Updated At" value={formatDateTime(selectedVoucher.updated_at)} />
              {selectedVoucher.message_id && (
                <div className="mb-4">
                  <p className="mb-1 text-xs text-muted-foreground">Message ID</p>
                  <div className="flex items-center gap-2">
                    <span>{selectedVoucher.message_id}</span>
                    <CopyButton
                      text={`rfc822msgid:${selectedVoucher.message_id}`}
                      label="Message ID"
                      size="small"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Voucher</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this voucher? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={useDialogOpen} onOpenChange={setUseDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Voucher as Used</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark this voucher as used?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleMarkUsed}>Mark as Used</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
